import { useMemo } from 'react'
import { formatTime, formatPace } from '../../lib/calculations'
import { calculateSegmentTimeline } from '../../lib/timeline'
import { Segment, AidStation, CalibrationProfile } from '../../types'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'

interface SharedSegmentsViewProps {
  segments: Segment[]
  aidStations?: AidStation[]
  calibrationProfile?: CalibrationProfile | null
}

export function SharedSegmentsView({ segments, aidStations = [], calibrationProfile }: SharedSegmentsViewProps) {
  // Create integrated timeline with both segments and aid stations
  const { segmentData, timeline } = useMemo(() => {
    if (!segments) return { segmentData: [], timeline: [] }

    const timelineResult = calculateSegmentTimeline(
      segments,
      aidStations,
      calibrationProfile || undefined
    )

    let cumulativeElevGain = 0
    let cumulativeElevLoss = 0

    const segmentData = timelineResult.segments.map((segmentData) => {
      const segment = segmentData.segment
      cumulativeElevGain += segment.elev_gain_m || 0
      cumulativeElevLoss += segment.elev_loss_m || 0

      return {
        ...segment,
        cumulativeDistance: segmentData.cumulativeDistance,
        cumulativeTime: segmentData.departureTime, // Includes aid station stops!
        cumulativeElevGain,
        cumulativeElevLoss,
        pace: segmentData.pace,
        segmentTimeMinutes: segmentData.travelTime,
        timeWithStop: segmentData.totalTime
      }
    })

    // Create merged timeline for display
    const items: Array<{ type: 'segment' | 'aid_station', data: any, distance: number }> = []

    // Add segments - position at their END distance (when completed)
    segmentData.forEach((segment, index) => {
      items.push({
        type: 'segment',
        data: { ...segment, index },
        distance: segment.cumulativeDistance
      })
    })

    // Add aid stations - position at their actual distance
    timelineResult.aidStations.forEach(aidStationData => {
      items.push({
        type: 'aid_station',
        data: aidStationData,
        distance: aidStationData.station.at_distance_km
      })
    })

    // Sort everything by distance for chronological timeline
    const sortedTimeline = items.sort((a, b) => a.distance - b.distance)

    return { segmentData, timeline: sortedTimeline }
  }, [segments, aidStations, calibrationProfile])

  const totalDistance = segmentData[segmentData.length - 1]?.cumulativeDistance || 0
  const totalTime = segmentData[segmentData.length - 1]?.cumulativeTime || 0
  const totalElevGain = segmentData[segmentData.length - 1]?.cumulativeElevGain || 0
  const totalElevLoss = segmentData[segmentData.length - 1]?.cumulativeElevLoss || 0

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal">
      <div className="p-8">
        <h3 className="text-2xl font-bold text-primary-900 mb-6">Course Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-6">
          <div>
            <p className="text-neutral-600">Total Distance</p>
            <p className="font-semibold text-lg text-primary-900">{totalDistance.toFixed(1)} km</p>
          </div>
          <div>
            <p className="text-neutral-600">Estimated Time</p>
            <p className="font-semibold text-lg text-primary-900">{formatTime(totalTime)}</p>
          </div>
          <div>
            <p className="text-neutral-600">Elevation Gain</p>
            <p className="font-semibold text-lg text-primary-900">{totalElevGain.toFixed(0)} m</p>
          </div>
          <div>
            <p className="text-neutral-600">Elevation Loss</p>
            <p className="font-semibold text-lg text-primary-900">{totalElevLoss.toFixed(0)} m</p>
          </div>
        </div>
      </div>
      <div className="border-t border-neutral-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Segment</TableHead>
              <TableHead className="text-right">Distance</TableHead>
              <TableHead className="text-right">Cumulative</TableHead>
              <TableHead className="text-right">Elev +/-</TableHead>
              <TableHead className="text-right">Pace</TableHead>
              <TableHead className="text-right">Time</TableHead>
              <TableHead className="text-right">Stop</TableHead>
              <TableHead className="text-right">Total Time</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {timeline.map((item, timelineIndex) => {
              if (item.type === 'segment') {
                const segment = item.data
                return (
                  <TableRow key={`segment-${segment.id}`}>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {segment.label || `Segment ${segment.index + 1}`}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {segment.distance_km.toFixed(1)} km
                    </TableCell>
                    <TableCell className="text-right">
                      {segment.cumulativeDistance.toFixed(1)} km
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="text-sm">
                        <div className="text-green-600">+{segment.elev_gain_m || 0}m</div>
                        <div className="text-red-600">-{segment.elev_loss_m || 0}m</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {formatPace(segment.pace)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatTime(segment.segmentTimeMinutes)}
                    </TableCell>
                    <TableCell className="text-right">
                      {segment.planned_stop_min ? formatTime(segment.planned_stop_min) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatTime(segment.cumulativeTime)}
                    </TableCell>
                  </TableRow>
                )
              } else {
                // Aid station row
                const aidStationData = item.data
                const station = aidStationData.station
                return (
                  <TableRow key={`aid-station-${station.id}`} className="bg-blue-50/50 border-blue-100">
                    <TableCell>
                      <div>
                        <div className="font-medium text-blue-700">
                          🏁 {station.name}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-neutral-500">
                      Aid Station
                    </TableCell>
                    <TableCell className="text-right">
                      {station.at_distance_km.toFixed(1)} km
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="text-sm text-neutral-500">
                        {station.available?.length ?
                          `${station.available.length} items` :
                          'Basic supplies'
                        }
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-neutral-500">
                      -
                    </TableCell>
                    <TableCell className="text-right text-neutral-500">
                      -
                    </TableCell>
                    <TableCell className="text-right">
                      {aidStationData.stopTime > 0 ? formatTime(aidStationData.stopTime) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatTime(aidStationData.arrivalTime)}
                    </TableCell>
                  </TableRow>
                )
              }
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}